/**
 * Absence Details Modal
 * 
 * Shows full details of an absence entry including:
 * - Absence type
 * - Total hours and days covered
 * - Absence reason (if provided)
 * - Absence ID from HR system
 */

import React from 'react';
import { Calendar, Clock } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { getAbsenceTypeLabel, type AbsenceType } from '@/data/mockAbsenceData';

interface AbsenceDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry: {
    absenceType: AbsenceType;
    absenceReason?: string;
    approvedBy?: string;
    approvedAt?: Date;
    absenceId?: string;
    hours: {
      monday: number;
      tuesday: number;
      wednesday: number;
      thursday: number;
      friday: number;
      saturday: number;
      sunday: number;
    };
    sourceIntegration?: string;
  };
}

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function AbsenceDetailsModal({
  open,
  onOpenChange,
  entry,
}: AbsenceDetailsModalProps) {
  const totalHours = DAYS.reduce(
    (sum, day) => sum + entry.hours[day],
    0
  );

  const daysWithHours = DAYS.filter(day => entry.hours[day] > 0);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[600px] flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4">
          <SheetTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-purple-600" />
            Absence Details
          </SheetTitle>
          <SheetDescription>
            Information from your HR system ({entry.sourceIntegration === 'people-hr' ? 'People HR' : 'HR System'})
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 space-y-4 pb-24">
          {/* Absence Type */}
          <div>
            <h4 className="text-sm font-semibold mb-2">
              Absence Information
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Type:</span>
                <Badge variant="secondary" className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100">
                  {getAbsenceTypeLabel(entry.absenceType)}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Hours:</span>
                <span className="font-medium">{totalHours}h</span>
              </div>
            </div>

            {/* Hours Breakdown */}
            <div className="mt-3 p-3 bg-purple-50/50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800">
              <div className="text-xs font-medium text-muted-foreground mb-2">
                Days Covered:
              </div>
              <div className="flex flex-wrap gap-2">
                {DAYS.map((day, index) => {
                  const hours = entry.hours[day];
                  if (hours === 0) return null;
                  return (
                    <Badge
                      key={day}
                      variant="outline"
                      className="text-xs border-purple-300 dark:border-purple-700"
                    >
                      {DAY_LABELS[index]}: {hours}h
                    </Badge>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Reason - Only show if provided */}
          {entry.absenceReason && (
            <>
              <Separator />
              <div>
                <h4 className="text-sm font-semibold mb-2">Reason:</h4>
                <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                  {entry.absenceReason}
                </p>
              </div>
            </>
          )}

          {/* Absence ID */}
          <Separator />
          <div>
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              Reference
            </h4>
            {entry.absenceId ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Absence ID:</span>
                  <code className="text-xs font-mono bg-muted px-2 py-1 rounded">
                    {entry.absenceId}
                  </code>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No absence ID available. Please refer to your HR system for more details.
              </p>
            )}
          </div>

          {/* Info Message */}
          <Separator />
          <Alert className="bg-purple-50/50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800">
            <AlertDescription className="text-sm text-purple-900 dark:text-purple-100">
              This entry is managed by your HR system and cannot be edited here. 
              Contact HR if you need to make changes.
            </AlertDescription>
          </Alert>
        </div>

        <SheetFooter className="absolute bottom-0 left-0 right-0 border-t bg-background p-6">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full"
          >
            Close
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
